import { useLazyQuery, useMutation } from "@apollo/client"
import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import BlockContent from "@pathwright/web/src/modules/blocks/BlockContent"
import PUBLISH_BLOCKS_CONTENT from "@pathwright/web/src/modules/blocks/graphql/publish-blocks-content-mutation"
import { updateCachedPathItemBlocks } from "@pathwright/web/src/modules/blocks/utils"
import useClipboard from "@pathwright/web/src/modules/clipboard/useClipboard"
import { COHORT_STATUS_COMPLETED } from "@pathwright/web/src/modules/group/constants"
// import { useSyncPlanContext } from "@pathwright/web/src/modules/path/sync/SyncPlanContext"
import { shouldRevealCompletionFeedback } from "@pathwright/web/src/modules/path/utils"
import USER_QUERY from "@pathwright/web/src/modules/user/graphql/user-query"
import { captureMessage, withScope } from "@sentry/browser"
import { ConnectStores } from "lib/core/store"
import get from "lodash/get"
import { LEARN } from "path/constants"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { ALLOW_RESETS } from "step/constants"
import { getCohortCompletedMessage } from "../../path/utils"
import { getCompletionResetAfterFailedCompletionAttempt } from "../../step/StepContainer"

const getNeedsForceUnsubmit = ({ step, completion, prevCompletion }) => {
  let needsForcedUnsubmit = false

  if (step && completion && prevCompletion) {
    // completion reset by user
    const completionResetByUser =
      prevCompletion.is_complete === true && completion.is_complete === false

    const completionResetAfterFailedCompletionAttempt =
      getCompletionResetAfterFailedCompletionAttempt({
        completion,
        prevCompletion
      })

    needsForcedUnsubmit =
      completionResetByUser ||
      (completionResetAfterFailedCompletionAttempt &&
        step.allow_resets === ALLOW_RESETS.ALWAYS)
  }

  return needsForcedUnsubmit
}

// Get copy props for BlockContent.
// The user may be unauthenticated, but that's OK as useClipboard
// and our firebase utils properly handle that case.
function useBlockCopyClipboard() {
  const clipboard = useClipboard()
  const copiedBlock = clipboard.getClip({ object_type: "block" })
  const copyBlock = (copiedBlockClip = null) => {
    // A falsey value for copiedBlockData will clear the clipboard
    if (!copiedBlockClip) {
      return clipboard.clear({
        object_type: "block"
      })
    }

    clipboard.copy(copiedBlockClip)
  }

  return {
    copyBlock,
    copiedBlock
  }
}

const BlocksContainer = ({
  step,
  mode: blocksMode,
  pathMode,
  onSave,
  handleBlocksCompletion
}) => {
  const [needsForcedUnsubmit, setNeedsForcedUnsubmit] = useState(false)
  const [blocksFirebaseContent, setBlocksFirebaseContent] = useState(null)
  const { t } = useTranslate()
  const [updateBlocksContent] = useMutation(PUBLISH_BLOCKS_CONTENT)
  // const { refetchSyncPlanQueries } = useSyncPlanContext()
  const [getUser] = useLazyQuery(USER_QUERY)
  const { copyBlock, copiedBlock } = useBlockCopyClipboard()

  // handle forcing unsubmit of progressable blocks
  usePreviousEffect(
    ([prevCompletion]) => {
      if (
        !needsForcedUnsubmit &&
        getNeedsForceUnsubmit({
          step,
          completion: step.completion,
          prevCompletion
        })
      ) {
        setNeedsForcedUnsubmit(true)
      }
    },
    [step.completion]
  )

  useEffect(() => {
    // Ensure that when a step's content is first created,
    //  we show the Step Publish button.
    if (step?.content_id && blocksFirebaseContent && blocksMode === "edit") {
      handleUpdateBlocksContent(blocksFirebaseContent)
    }
  }, [step?.content_id, blocksFirebaseContent])

  // Determine if blocks should hide learner feedback (ex: Multiple Choice answers)
  const getShouldHideFeedback = (user, step) => {
    // base check
    if (shouldRevealCompletionFeedback(user, step)) return false

    // staff always sees feedback and answers
    if (pathMode > LEARN) return false

    return true
  }

  const getUserByID = async (id) => {
    try {
      return await getUser({ variables: { id } })
    } catch (error) {
      console.error("Error fetching user:", error)
    }
  }

  const getBlocksConfig = () => {
    // TODO: Use a Type for this.
    const blocksContext = {
      step: step,
      user: window.App.getStore("auth").getState().user,
      school: window.App.getStore("school").getState().school,
      resource: window.App.getStore("resource").getState().resource,
      group: window.App.getStore("resource").getState().offering,
      path: window.App.getStore("path").getState().path,
      registration: window.App.getStore("resource").getState().registration
    }

    // TODO: plug this back in once templates  aren't broken
    // const template = `blocks-template-${step.step_verb
    //   .replace("-", "")
    //   .toLowerCase()}`

    // TODO: enable based on school's subscription level
    //   -- not implemented yet
    const enableVideo = true

    const failed =
      pathMode <= LEARN &&
      step.completion.has_failed_passing_attempt &&
      step.allow_resets === ALLOW_RESETS.BEFORE

    // Disable learner retries in blocks (either by prop or completion status)
    const disableInput =
      failed ||
      (step.completion.is_complete && pathMode <= LEARN) ||
      (blocksContext.group.status === COHORT_STATUS_COMPLETED &&
        pathMode <= LEARN)

    // Hide Block assessment feedback?
    const hideAssessmentFeedback = getShouldHideFeedback(
      blocksContext.user,
      step
    )

    const inputDisabledMessage =
      getCohortCompletedMessage(t) || "Input is currently disabled."

    // Assume the user is the current user unless we have a completion
    const userID =
      get(step.completion, "user.id") ||
      window.App.getStore("auth").getState().user?.id

    return {
      contextKey: `/${step.sync_source_id}`,
      userID,
      mode: blocksMode.toUpperCase(),
      templateVariables: blocksContext,
      accountID: blocksContext.school.id,
      isSuperuser: blocksContext.user.is_superuser,
      enableVideo,
      disableInput,
      inputDisabledMessage,
      needsForcedUnsubmit: needsForcedUnsubmit && !failed,
      hideAssessmentFeedback,
      theme: {},
      getUserByID,
      copyBlock,
      copiedBlock
      // theme: theme || {}
    }
  }

  const handleBlockProgress = (completion) => {
    if (
      completion?.progress === 1 &&
      !completion?.completionToken &&
      blocksMode === "view"
    ) {
      const user = window.App.getStore("auth").getState().user
      const logData = {
        completion,
        step,
        user
      }
      console.error("No completion token found for block completion", logData)

      // Since this is logging quite often, let's only log 5% of the time this occurs.
      withScope((scope) => {
        scope.setExtra(`blocksCompletion:step`, step)
        scope.setExtra(`blocksCompletion:completion`, completion)
        scope.setExtra(`blocksCompletion:user`, user)
        captureMessage("Blocks progress updated with no token")
      })
    }

    if (
      completion.progress != null &&
      completion.progress <= 0 &&
      needsForcedUnsubmit
    ) {
      // assumption is that the blocks progress has been cleared due to a forced unsubmit
      setNeedsForcedUnsubmit(false)
    }

    handleBlocksCompletion(completion)
  }

  const handleUpdateBlocksContent = async (content) => {
    const { lastPublishedDateTime, lastModifiedDateTime } = content

    // Convert to ISO string for Django
    const lastPublished = lastPublishedDateTime
      ? new Date(lastPublishedDateTime).toISOString()
      : null
    const contentLastModified = lastModifiedDateTime
      ? new Date(lastModifiedDateTime).toISOString()
      : new Date().toISOString()

    if (!step?.content_id) return

    const payload = {
      id: step.content_id,
      stepId: step.id,
      contentLastModified,
      lastPublished
    }

    updateCachedPathItemBlocks(step, {
      content_last_modified: contentLastModified,
      last_published: lastPublished
    })

    await updateBlocksContent({
      variables: payload
    })

    // Is this necessary?
    // if (refetchSyncPlanQueries) refetchSyncPlanQueries()
  }

  const handleSaveContent = async ({ content }) => {
    // `onSave` updates the content admin store via StepContainer.js.
    //    If this is new content, the backend create a new content object and return it as step.content.
    //    If not new content, the content admin store ignores it.
    if (onSave) onSave({ content })

    // We store the latest Blocks (Firebase) content here to update the Apollo cache after saving.
    // See useEffect above
    setBlocksFirebaseContent(content)
  }

  // Only use certain features when user is authenticated.
  return (
    <BlockContent
      {...getBlocksConfig()}
      onSave={handleSaveContent}
      onProgress={handleBlockProgress}
    />
  )
}

BlocksContainer.displayName = "BlocksContainer"

BlocksContainer.propTypes = {
  mode: PropTypes.oneOf(["view", "edit"]),
  onSave: PropTypes.func,
  handleBlocksCompletion: PropTypes.func
}

BlocksContainer.defaultProps = {
  mode: "edit"
}

const mapStateToProps = (state, ownProps) => {
  const { mode } = state.pathStore.state

  return _.assign(
    {
      pathMode: mode
    },
    ownProps
  )
}

const ConnectedBlocksContainer = ConnectStores(
  BlocksContainer,
  [{ store: "path", watch: ["mode"] }],
  {},
  mapStateToProps
)

export default ConnectedBlocksContainer
